import { type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import { buildAiReportExportUrl } from '../utils/reportExportUrls'
import type { ReportHubLink } from '../types'
import type { ReportHubNavLinkProps } from '../types/components'

const DEFAULT_LINK_CLASS =
  'text-[#007BFF] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring'

/** Whether the hub entry can render a target (e.g. AI report needs auth token). */
export function canRenderReportHubLink(link: ReportHubLink): boolean {
  switch (link.kind) {
    case 'react':
      return Boolean(link.reactPath)
    case 'dashboard':
      return Boolean(link.dashboardPath)
    case 'external':
      if (link.descriptionKey === 'reports_ai') return Boolean(buildAiReportExportUrl())
      return Boolean(link.externalHref)
    default:
      return false
  }
}

function LinkBody({
  link,
  children,
  showExternalIcon,
}: {
  link: ReportHubLink
  children?: ReactNode
  showExternalIcon?: boolean
}) {
  if (children) return <>{children}</>
  return (
    <>
      {link.label}
      {showExternalIcon ? (
        <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
      ) : null}
    </>
  )
}

export function ReportHubNavLink({
  link,
  tooltip,
  className,
  children,
}: ReportHubNavLinkProps) {
  if (!canRenderReportHubLink(link)) return null

  const linkClass = cn(className ?? DEFAULT_LINK_CLASS)

  switch (link.kind) {
    case 'react':
      if (!link.reactPath) return null
      return (
        <Link
          to={`/reports/${link.reactPath}`}
          title={tooltip}
          className={linkClass}
        >
          <LinkBody link={link}>{children}</LinkBody>
        </Link>
      )

    case 'dashboard':
      if (!link.dashboardPath) return null
      return (
        <Link to={link.dashboardPath} title={tooltip} className={linkClass}>
          <LinkBody link={link}>{children}</LinkBody>
        </Link>
      )

    case 'external': {
      const href =
        link.descriptionKey === 'reports_ai'
          ? buildAiReportExportUrl()
          : link.externalHref
      if (!href) return null
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={tooltip}
          className={cn(linkClass, !children && 'inline-flex items-center gap-1')}
        >
          <LinkBody link={link} showExternalIcon={!children}>
            {children}
          </LinkBody>
        </a>
      )
    }

    default:
      return null
  }
}
